import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiDollarSign, FiSave, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import { formatLocalDateTime } from '../utils/formatTimestamp';
import { appAlert, appConfirm } from '../utils/appDialog';

const CurrencySyncSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    schedule_enabled: false,
    schedule_cron: '0 6 * * *',
    provider: 'frankfurter',
    provider_url: '',
    base_currency: 'USD',
  });

  const { data: settings, isLoading } = useQuery(
    ['currencies-settings'],
    () => api.get('/currencies/settings').then((r) => r.data)
  );

  useEffect(() => {
    if (!settings) return;
    setForm({
      schedule_enabled: Boolean(settings.schedule_enabled),
      schedule_cron: settings.schedule_cron || '0 6 * * *',
      provider: settings.provider || 'frankfurter',
      provider_url: settings.provider_url || '',
      base_currency: settings.base_currency || 'USD',
    });
  }, [settings]);

  const saveMutation = useMutation((body) => api.put('/currencies/settings', body), {
    onSuccess: () => {
      queryClient.invalidateQueries(['currencies-settings']);
      void appAlert('Currency sync settings saved.');
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const syncMutation = useMutation(() => api.post('/currencies/sync'), {
    onSuccess: (res) => {
      queryClient.invalidateQueries(['currencies-settings']);
      queryClient.invalidateQueries(['currencies']);
      void appAlert(`Synced ${res.data?.count ?? 0} currencies from ${res.data?.source || 'provider'}.`);
    },
    onError: (e) => void appAlert(e.response?.data?.details || e.response?.data?.error || e.message),
  });

  const handleSave = (e) => {
    e.preventDefault();
    saveMutation.mutate({
      schedule_enabled: false,
      provider: form.provider,
      provider_url: form.provider_url.trim(),
      base_currency: form.base_currency.trim().toUpperCase(),
    });
  };

  if (isLoading) {
    return <div className="loading">Loading currency settings…</div>;
  }

  const providers = settings?.providers || [];

  return (
    <div className="settings-section currency-sync-settings">
      <div className="settings-section-header">
        <h2>
          <FiDollarSign /> Currency sync
        </h2>
        <p>
          Pull country/currency/conversion rates from a public FX API into the Metadata → Currency
          table. Use Sync now to refresh — this is not run by Cloudflare cron.
        </p>
      </div>

      <form className="settings-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="fx-provider">Provider</label>
          <select
            id="fx-provider"
            value={form.provider}
            onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <small className="form-hint">
            Default endpoint:{' '}
            {providers.find((p) => p.id === form.provider)?.default_url ||
              settings?.default_provider_url}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="fx-provider-url">Provider URL override (optional)</label>
          <input
            id="fx-provider-url"
            type="url"
            placeholder="Leave blank to use the provider default"
            value={form.provider_url}
            onChange={(e) => setForm((f) => ({ ...f, provider_url: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label htmlFor="fx-base">Base currency</label>
          <input
            id="fx-base"
            type="text"
            maxLength={3}
            value={form.base_currency}
            onChange={(e) => setForm((f) => ({ ...f, base_currency: e.target.value.toUpperCase() }))}
            required
          />
          <small className="form-hint">
            Rates are stored as: 1 {form.base_currency || 'BASE'} = rate × quote currency.
          </small>
        </div>

        <div className="currency-last-run">
          <div>
            <span>Last sync</span>
            <strong>
              {settings?.last_synced_at
                ? formatLocalDateTime(settings.last_synced_at)
                : 'Never'}
            </strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{settings?.last_sync_status || '—'}</strong>
          </div>
          <div>
            <span>Rows</span>
            <strong>{settings?.last_sync_count ?? 0}</strong>
          </div>
        </div>
        {settings?.last_sync_error && (
          <p className="form-error">{settings.last_sync_error}</p>
        )}

        <div className="settings-form-actions">
          <LoadingButton
            type="submit"
            className="btn btn-primary"
            loading={saveMutation.isLoading}
            icon={FiSave}
          >
            Save settings
          </LoadingButton>
          <LoadingButton
            type="button"
            className="btn btn-secondary"
            loading={syncMutation.isLoading}
            icon={FiRefreshCw}
            onClick={async () => {
              if (await appConfirm('Pull rates now and overwrite the currencies table?')) {
                syncMutation.mutate();
              }
            }}
          >
            Sync now
          </LoadingButton>
          <Link to="/metadata?type=currencies" className="btn btn-secondary">
            View currencies <FiExternalLink aria-hidden />
          </Link>
        </div>
      </form>
    </div>
  );
};

export default CurrencySyncSettings;
